import { useEffect, useState } from 'react'
import { Star, X } from 'lucide-react'
import { Button } from '../../components/ui/Button.tsx'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card.tsx'
import LoadingSpinner from '../../components/ui/LoadingSpinner.tsx'
import { apiGet, apiPostForm } from '../../lib/api.ts'

interface Timesheet {
  id: number
  shift_date: string
  period_start_date: string
  period_end_date: string
  entry_count: number
  hours_worked: number
  break_minutes: number
  status: string
  is_duplicate: boolean
  duplicate_count: number
  rejection_type: string
  rejection_category: string
  rejection_reason: string
  submitted_at: string
  title: string
  first_name: string
  last_name: string
  entries: Array<{
    id: number
    shift_date: string
    hours_worked: number
    break_minutes: number
  }>
}

function formatDateLabel(value: string): string {
  if (!value) {
    return ''
  }

  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

function formatDateRange(startDate: string, endDate: string): string {
  if (!startDate) {
    return ''
  }

  if (!endDate || endDate === startDate) {
    return formatDateLabel(startDate)
  }

  return `${formatDateLabel(startDate)} to ${formatDateLabel(endDate)}`
}

export default function TimesheetsPage() {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([])
  const [pendingApprovals, setPendingApprovals] = useState(0)
  const [duplicateWarning, setDuplicateWarning] = useState('')
  const [loading, setLoading] = useState(true)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const [reviewingTimesheet, setReviewingTimesheet] = useState<Timesheet | null>(null)
  const [rating, setRating] = useState(0)
  const [comments, setComments] = useState('')
  const [submittingReview, setSubmittingReview] = useState(false)

  const load = async () => {
    try {
      const data = await apiGet<{ pending_approvals: number; timesheets: Timesheet[]; duplicate_timesheet_warning: string }>(
        '/practice/timesheets.php?format=json'
      )
      setPendingApprovals(data.pending_approvals)
      setTimesheets(data.timesheets)
      setDuplicateWarning(data.duplicate_timesheet_warning)
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load timesheets.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  useEffect(() => {
    if (!reviewingTimesheet) {
      return
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submittingReview) {
        setReviewingTimesheet(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [reviewingTimesheet, submittingReview])

  const handleAction = async (timesheetId: number, action: 'approve_timesheet' | 'reject_timesheet') => {
    setNotice('')
    try {
      const data = await apiPostForm<{ message: string }>('/practice/timesheets.php?format=json', {
        action,
        timesheet_id: String(timesheetId),
      })
      setNotice(data.message)
      await load()
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Unable to update timesheet.')
    }
  }

  const handleApproveClick = (timesheet: Timesheet) => {
    setNotice('')
    setError('')
    setComments('')
    setRating(0)
    setReviewingTimesheet(timesheet)
  }

  const handleApproveWithReview = async () => {
    if (!reviewingTimesheet) {
      return
    }

    setSubmittingReview(true)
    setNotice('')

    try {
      const data = await apiPostForm<{ message: string }>('/practice/timesheets.php?format=json', {
        action: 'approve_timesheet',
        timesheet_id: String(reviewingTimesheet.id),
        rating: String(rating),
        comments,
      })
      setNotice(data.message)
      setReviewingTimesheet(null)
      setComments('')
      setRating(0)
      await load()
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Unable to approve timesheet.')
    } finally {
      setSubmittingReview(false)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-text-900">Timesheets</h1>
        <p className="mt-1 text-sm text-text-600">{pendingApprovals} pending approvals</p>
      </div>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && <div className="rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{notice}</div>}
      {duplicateWarning && <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">{duplicateWarning}</div>}

      {reviewingTimesheet && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/45 p-0 sm:items-center sm:p-4">
          <div
            className="absolute inset-0"
            onClick={() => {
              if (!submittingReview) {
                setReviewingTimesheet(null)
              }
            }}
            aria-hidden="true"
          />
          <Card className="relative z-10 w-full rounded-t-3xl border-brand-300 shadow-2xl sm:max-w-xl sm:rounded-2xl">
            <CardHeader className="space-y-3 border-b border-surface-200 pb-4">
              <div className="mx-auto h-1.5 w-14 rounded-full bg-surface-300 sm:hidden" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle>Review worker before approval</CardTitle>
                  <p className="mt-2 text-sm text-text-600">
                    Add a rating before the timesheet is approved and locked.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-text-500"
                  onClick={() => setReviewingTimesheet(null)}
                  disabled={submittingReview}
                  aria-label="Close review panel"
                >
                  <X size={18} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pb-6 pt-5">
              <div>
                <p className="font-semibold text-text-900">{reviewingTimesheet.title || 'Placement'}</p>
                <p className="text-sm text-text-600">
                  {[reviewingTimesheet.first_name, reviewingTimesheet.last_name].filter(Boolean).join(' ')}
                </p>
                  <p className="mt-1 text-sm text-text-500">
                    {formatDateRange(reviewingTimesheet.period_start_date, reviewingTimesheet.period_end_date)}
                  </p>
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold text-text-900">Rating *</p>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      className="rounded-full p-1"
                      onClick={() => setRating(value)}
                      aria-label={`Rate ${value} star${value === 1 ? '' : 's'}`}
                    >
                      <Star
                        size={30}
                        className={value <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <label className="block space-y-2">
                <span className="text-sm font-semibold text-text-900">Comments</span>
                <textarea
                  className="w-full rounded-md border border-surface-600 px-3 py-3"
                  rows={4}
                  maxLength={250}
                  value={comments}
                  onChange={(event) => setComments(event.target.value)}
                  placeholder="Optional comments about the worker"
                />
                <span className="block text-right text-xs text-text-500">{comments.length}/250</span>
              </label>

              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => setReviewingTimesheet(null)} disabled={submittingReview}>
                  Cancel
                </Button>
                <Button
                  className="bg-brand-600 hover:bg-brand-700"
                  onClick={() => void handleApproveWithReview()}
                  disabled={submittingReview || rating < 1}
                >
                  {submittingReview ? 'Approving...' : 'Submit Review & Approve'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Submitted hours</CardTitle>
        </CardHeader>
        <CardContent>
          {timesheets.length === 0 ? (
            <p className="text-text-700">No timesheets have been submitted yet.</p>
          ) : (
            <div className="space-y-3">
              {timesheets.map((timesheet) => (
                <div key={timesheet.id} className="rounded-lg border border-surface-300 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-text-900">{timesheet.title || 'Placement'}</h3>
                      <p className="text-sm text-text-600">{[timesheet.first_name, timesheet.last_name].filter(Boolean).join(' ')}</p>
                    </div>
                    <span className="rounded-full bg-surface-100 px-3 py-1 text-xs font-semibold capitalize text-text-700">
                      {timesheet.status}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-text-600">
                    <div>{formatDateRange(timesheet.period_start_date, timesheet.period_end_date)}</div>
                    <div>{timesheet.hours_worked.toFixed(2)} hrs</div>
                    <div>{timesheet.entry_count} day{timesheet.entry_count === 1 ? '' : 's'}</div>
                    <div>{timesheet.break_minutes} mins break total</div>
                  </div>
                  <div className="mt-3 rounded-lg bg-surface-50 px-3 py-3">
                    <div className="space-y-2 text-sm text-text-700">
                      {timesheet.entries.map((entry) => (
                        <div key={entry.id || `${timesheet.id}-${entry.shift_date}`} className="flex items-center justify-between gap-3">
                          <span>{formatDateLabel(entry.shift_date)}</span>
                          <span>
                            {entry.hours_worked.toFixed(2)} hrs · {entry.break_minutes} mins break
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {timesheet.is_duplicate && (
                    <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      Duplicate detected: {timesheet.duplicate_count} entries exist for this placement and date.
                    </div>
                  )}
                  {timesheet.rejection_reason && (
                    <div className={`mt-3 rounded-md px-3 py-2 text-sm ${timesheet.rejection_type === 'resubmit' ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
                      <p className="font-semibold">
                        {timesheet.rejection_type === 'resubmit' ? 'Admin requested locum resubmission' : 'Removed by admin'}
                      </p>
                      <p>
                        {timesheet.rejection_category ? `${timesheet.rejection_category}: ` : ''}
                        {timesheet.rejection_reason}
                      </p>
                    </div>
                  )}
                  {timesheet.status === 'submitted' && (
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <Button variant="outline" onClick={() => void handleAction(timesheet.id, 'reject_timesheet')}>
                        Reject
                      </Button>
                      <Button className="bg-brand-600 hover:bg-brand-700" onClick={() => handleApproveClick(timesheet)}>
                        Approve
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
